import {
  purposes,
  type Component,
  type Purpose,
  type TextValue,
} from '@/api/core/native-presentation';
import { Button, FormField, Input, Textarea } from '@mgrvtechgroup/admin-ds';
import { Fragment } from 'react';
import { languageLabel } from '../media/media-presentation';
import { Choice } from './choice';
import {
  CATEGORY_ORDER,
  CATEGORY_TITLE,
  COMPONENT_CATEGORY,
  KIND_TITLE,
  PURPOSE_CATEGORY,
  PURPOSE_ORDER,
  purposeLabel,
  type CategoryId,
} from './presentation-categories';

const LANGUAGES: TextValue['language'][] = ['ko', 'en', 'und'];
// 문단 길이의 값은 여러 줄 입력으로 보여 줘야 전문을 읽고 고칠 수 있다.
const LONG_PURPOSES = new Set<Purpose>([
  'TAGLINE',
  'CARD_DESCRIPTION',
  'DESCRIPTION',
  'NEIGHBORHOOD_DESCRIPTION',
  'BUILDING_OVERVIEW',
  'NOTICE_SELECT',
  'NOTICE_CONFIRM',
  'SOURCE_NOTICE',
  'PARKING_INFO',
  'COMPONENT_DESCRIPTION',
]);
const FLAGS = [
  ['is_active', '사용'],
  ['show_on_detail', '상세 표시'],
  ['show_on_search', '목록 표시'],
] as const;

interface RowGroup {
  key: string;
  purpose: Purpose;
  byLanguage: Partial<Record<TextValue['language'], number>>;
}

/** (용도, 항목 키)가 같은 문구는 같은 항목의 언어별 값이라 한 줄에 나란히 놓는다. */
function groupRows(texts: TextValue[], indexes: number[]): RowGroup[] {
  const rows = new Map<string, RowGroup>();
  for (const i of indexes) {
    const t = texts[i];
    if (!t) continue;
    const key = `${t.purpose}:${t.item_key}`;
    const row = rows.get(key) ?? { key, purpose: t.purpose, byLanguage: {} };
    row.byLanguage[t.language] = i;
    rows.set(key, row);
  }
  return [...rows.values()].sort(
    (a, b) => PURPOSE_ORDER.indexOf(a.purpose) - PURPOSE_ORDER.indexOf(b.purpose),
  );
}

export interface PresentationEditFormProps {
  scope: string;
  locked: boolean;
  name: string;
  onName: (value: string) => void;
  texts: TextValue[];
  setTexts: (next: TextValue[]) => void;
  removed: number[];
  setRemoved: (next: number[]) => void;
  components: Component[];
  setComponents: (next: Component[]) => void;
  removedComponents: string[];
  setRemovedComponents: (next: string[]) => void;
  newPurpose: Purpose;
  setNewPurpose: (next: Purpose) => void;
  language: TextValue['language'];
  setLanguage: (next: TextValue['language']) => void;
  /** 값이 바뀌면 이미 열려 있던 영향 확인을 닫는다. */
  touch: () => void;
}

/** 수정 창의 입력 폼 — 읽기 화면과 같은 범주 제목 아래에 문구·공간 안내를 모아 둔다. */
export function PresentationEditForm(p: PresentationEditFormProps) {
  const { scope, locked, texts, components } = p;
  const keys = new Set(components.map((c) => c.key).filter((k): k is string => k !== null));
  const setText = (i: number, patch: Partial<TextValue>) => {
    p.setTexts(texts.map((x, n) => (n === i ? { ...x, ...patch } : x)));
    p.touch();
  };
  const setComponent = (i: number, patch: Partial<Component>) => {
    p.setComponents(components.map((x, n) => (n === i ? { ...x, ...patch } : x)));
    p.touch();
  };
  const textIndexesOf = (key: string | null) =>
    texts.flatMap((t, i) => (key !== null && t.component_key === key ? [i] : []));
  const looseIndexes = texts.flatMap((t, i) =>
    !t.component_key || !keys.has(t.component_key) ? [i] : [],
  );
  const titleOf = (c: Component) => {
    const name = textIndexesOf(c.key)
      .map((i) => texts[i])
      .find(
        (t) => t && (t.purpose === 'COMPONENT_NAME' || t.purpose === 'STATION_NAME') && t.value,
      );
    return name?.value ?? KIND_TITLE[c.kind];
  };

  function renderTextCell(i: number) {
    const t = texts[i];
    if (!t) return null;
    const full = `${purposeLabel(t.purpose)} · ${languageLabel(t.language)}${t.component_key ? ' · 공간 안내' : ''}`;
    return (
      <div className="min-w-0 space-y-1">
        <FormField label={languageLabel(t.language)} htmlFor={`${scope}-text-${String(i)}`}>
          {LONG_PURPOSES.has(t.purpose) ? (
            <Textarea
              id={`${scope}-text-${String(i)}`}
              aria-label={full}
              rows={3}
              value={t.value ?? ''}
              disabled={locked || p.removed.includes(i) || t.value === null}
              onChange={(e) => {
                setText(i, { value: e.target.value });
              }}
            />
          ) : (
            <Input
              id={`${scope}-text-${String(i)}`}
              aria-label={full}
              value={t.value ?? ''}
              disabled={locked || p.removed.includes(i) || t.value === null}
              onChange={(e) => {
                setText(i, { value: e.target.value });
              }}
            />
          )}
        </FormField>
        <div className="body-sm flex flex-wrap gap-x-4 gap-y-1">
          <label>
            <input
              type="checkbox"
              disabled={locked}
              checked={t.value === null}
              onChange={(e) => {
                setText(i, { value: e.target.checked ? null : '' });
              }}
            />{' '}
            값 미설정
          </label>
          <label>
            <input
              type="checkbox"
              disabled={locked}
              checked={p.removed.includes(i)}
              onChange={(e) => {
                p.setRemoved(
                  e.target.checked ? [...p.removed, i] : p.removed.filter((n) => n !== i),
                );
                p.touch();
              }}
            />{' '}
            이 문구 제거
          </label>
        </div>
      </div>
    );
  }

  function renderTextRows(indexes: number[], repeatedLabel?: string) {
    return (
      <div className="space-y-4">
        {groupRows(texts, indexes).map((row) => (
          <div key={row.key} className="space-y-1">
            {purposeLabel(row.purpose) !== repeatedLabel && (
              <p className="body-sm font-medium">{purposeLabel(row.purpose)}</p>
            )}
            <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
              {LANGUAGES.map((language) => {
                const i = row.byLanguage[language];
                return i === undefined ? null : (
                  <Fragment key={language}>{renderTextCell(i)}</Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderControls(i: number) {
    const c = components[i];
    if (!c) return null;
    return (
      <div className="space-y-3">
        {c.key && (
          <label className="body-sm block">
            <input
              type="checkbox"
              checked={p.removedComponents.includes(c.key)}
              onChange={(e) => {
                const target = c.key;
                if (!target) return;
                const group = [
                  target,
                  ...components.filter((child) => child.parent_key === target).map((x) => x.key),
                ].filter((k): k is string => k !== null);
                p.setRemovedComponents(
                  e.target.checked
                    ? [...new Set([...p.removedComponents, ...group])]
                    : p.removedComponents.filter((k) => !group.includes(k)),
                );
                p.touch();
              }}
            />{' '}
            이 안내와 연결 문구 제거
          </label>
        )}
        <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {c.kind === 'LOCATION_LINE' && (
            <FormField label="교통 노선 코드" htmlFor={`${scope}-line-${String(i)}`}>
              <Input
                id={`${scope}-line-${String(i)}`}
                value={c.line_code ?? ''}
                onChange={(e) => {
                  setComponent(i, { line_code: e.target.value });
                }}
              />
            </FormField>
          )}
          <FormField label="안내 표시 순서" htmlFor={`${scope}-order-${String(i)}`}>
            <Input
              id={`${scope}-order-${String(i)}`}
              type="number"
              value={c.source_order ?? ''}
              onChange={(e) => {
                setComponent(i, {
                  source_order: e.target.value === '' ? null : Number(e.target.value),
                });
              }}
            />
          </FormField>
        </div>
        {c.kind === 'LOCATION_LINE' ? (
          <p className="body-sm text-muted-foreground">상위 위치 안내의 표시 설정을 따릅니다.</p>
        ) : (
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {FLAGS.map(([flag, text]) => (
              <label key={flag} className="body-sm">
                {text}{' '}
                <Choice
                  label={`${KIND_TITLE[c.kind]} ${String(i + 1)} ${text}`}
                  disabled={locked}
                  value={c[flag] == null ? 'null' : String(c[flag])}
                  options={{ null: '기본값', true: '표시', false: '숨김' }}
                  onChange={(v) => {
                    setComponent(i, { [flag]: v === 'null' ? null : v === 'true' });
                  }}
                />
              </label>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {c.kind === 'LOCATION_BULLET' && c.key && (
            <Button
              variant="outline"
              onClick={() => {
                p.setComponents([
                  ...components,
                  {
                    key: null,
                    kind: 'LOCATION_LINE',
                    parent_key: c.key,
                    source_order: null,
                    provenance: null,
                    line_code: null,
                  },
                ]);
                p.touch();
              }}
            >
              교통 노선 추가
            </Button>
          )}
          {c.key && (
            <Button
              variant="outline"
              onClick={() => {
                p.setTexts([
                  ...texts,
                  {
                    purpose: 'COMPONENT_NAME',
                    language: p.language,
                    item_key: crypto.randomUUID(),
                    value: '',
                    source_order: null,
                    component_key: c.key,
                  },
                ]);
                p.touch();
              }}
            >
              이 공간의 이름 추가
            </Button>
          )}
        </div>
      </div>
    );
  }

  function renderComponentCard(i: number) {
    const c = components[i];
    if (!c) return null;
    const lines = components.flatMap((child, n) =>
      child.kind === 'LOCATION_LINE' && c.key !== null && child.parent_key === c.key ? [n] : [],
    );
    return (
      <fieldset disabled={locked} className="space-y-3 rounded-md border p-3">
        <legend className="body-strong px-1">{titleOf(c)}</legend>
        {renderTextRows(textIndexesOf(c.key), titleOf(c))}
        {renderControls(i)}
        {lines.map((n) => (
          <fieldset
            key={components[n]?.key ?? `new-${String(n)}`}
            className="space-y-3 rounded-md border p-3"
          >
            <legend className="body-sm px-1">{KIND_TITLE.LOCATION_LINE}</legend>
            {renderControls(n)}
          </fieldset>
        ))}
      </fieldset>
    );
  }

  const topLevel = components.flatMap((c, i) =>
    c.kind === 'LOCATION_LINE' && c.parent_key && keys.has(c.parent_key) ? [] : [i],
  );
  const addComponent = (kind: 'COMMON_AREA' | 'LOCATION_BULLET') => {
    p.setComponents([
      ...components,
      {
        key: null,
        kind,
        parent_key: null,
        source_order: null,
        provenance: null,
        is_active: null,
        show_on_detail: null,
        show_on_search: null,
      },
    ]);
    p.touch();
  };
  const sections = CATEGORY_ORDER.map((id: CategoryId) => ({
    id,
    rows: looseIndexes.filter(
      (i) => (PURPOSE_CATEGORY[texts[i]?.purpose ?? 'DISPLAY_NAME'] ?? 'etc') === id,
    ),
    comps: topLevel.filter((i) => {
      const c = components[i];
      return c ? COMPONENT_CATEGORY[c.kind] === id : false;
    }),
  })).filter(
    (section) =>
      section.rows.length > 0 ||
      section.comps.length > 0 ||
      section.id === 'common' ||
      section.id === 'location',
  );

  return (
    <div className="space-y-8">
      <section aria-labelledby={`${scope}-cat-name`} className="space-y-3">
        <h4 id={`${scope}-cat-name`} className="title-sm border-b pb-1">
          관리 정보
        </h4>
        <FormField
          label="관리 이름"
          htmlFor={`${scope}-name`}
          message="홈페이지에 나가는 이름과 별도로 저장하는 내부용 이름입니다."
        >
          <Input
            id={`${scope}-name`}
            value={p.name}
            disabled={locked}
            onChange={(e) => {
              p.onName(e.target.value);
            }}
          />
        </FormField>
      </section>
      {sections.map((section) => (
        <section
          key={section.id}
          aria-labelledby={`${scope}-cat-${section.id}`}
          className="space-y-4"
        >
          <h4 id={`${scope}-cat-${section.id}`} className="title-sm border-b pb-1">
            {CATEGORY_TITLE[section.id]}
          </h4>
          {section.rows.length > 0 && renderTextRows(section.rows)}
          {section.comps.length > 0 && (
            <div className="grid gap-3">
              {section.comps.map((i) => (
                <Fragment key={components[i]?.key ?? `new-${String(i)}`}>
                  {renderComponentCard(i)}
                </Fragment>
              ))}
            </div>
          )}
          {section.id === 'common' && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                addComponent('COMMON_AREA');
              }}
            >
              {KIND_TITLE.COMMON_AREA} 추가
            </Button>
          )}
          {section.id === 'location' && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                addComponent('LOCATION_BULLET');
              }}
            >
              {KIND_TITLE.LOCATION_BULLET} 추가
            </Button>
          )}
        </section>
      ))}
      <section aria-labelledby={`${scope}-cat-add`} className="space-y-3">
        <h4 id={`${scope}-cat-add`} className="title-sm border-b pb-1">
          문구 추가
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          <label>
            문구 용도{' '}
            <Choice
              label="새 문구 용도"
              value={p.newPurpose}
              disabled={locked}
              options={purposes}
              onChange={(v) => {
                p.setNewPurpose(v as Purpose);
              }}
            />
          </label>
          <label>
            언어{' '}
            <Choice
              label="새 문구 언어"
              value={p.language}
              disabled={locked}
              options={{ ko: '한국어', en: '영어', und: '언어 미지정' }}
              onChange={(v) => {
                p.setLanguage(v as TextValue['language']);
              }}
            />
          </label>
          <Button
            variant="outline"
            disabled={locked}
            onClick={() => {
              p.setTexts([
                ...texts,
                {
                  purpose: p.newPurpose,
                  language: p.language,
                  item_key: crypto.randomUUID(),
                  value: '',
                  source_order: null,
                  component_key: null,
                },
              ]);
              p.touch();
            }}
          >
            문구 추가
          </Button>
        </div>
        <p className="caption text-muted-foreground">
          새 공용공간·위치 안내의 기본값은 공개입니다. 숨김을 명시하면 해당 화면에서 숨겨집니다. 새
          공간의 이름은 저장 후 추가할 수 있습니다.
        </p>
      </section>
    </div>
  );
}
